/**
 * Canonical tab ↔ path-segment mappings for Phase 2 subroutes.
 * Default tabs use a null segment (module root URL).
 */

/** @typedef {{ id: string, segment: string|null, label?: string }} TabDef */

function indexById(tabs) {
  const map = Object.create(null);
  for (const t of tabs) map[t.id] = t;
  return map;
}

function indexBySegment(tabs) {
  const map = Object.create(null);
  for (const t of tabs) {
    if (t.segment != null) map[t.segment] = t;
  }
  return map;
}

export const COCKPIT_TABS = [
  { id: 'overview', segment: null, label: 'Visão geral' },
  { id: 'health', segment: 'saude', label: 'Saúde' },
  { id: 'actions', segment: 'acoes', label: 'Ações' },
  { id: 'provisioning', segment: 'provisionamento', label: 'Provisionamento' },
  { id: 'users', segment: 'usuarios', label: 'Usuários' },
  { id: 'domains', segment: 'dominios', label: 'Domínios' },
  { id: 'logs', segment: 'logs', label: 'Logs' },
];

export const BILLING_TABS = [
  { id: 'overview', segment: null, label: 'Visão geral' },
  { id: 'assinaturas', segment: 'assinaturas', label: 'Assinaturas' },
  { id: 'faturas', segment: 'faturas', label: 'Faturas' },
  { id: 'comercial', segment: 'status-comercial', label: 'Status comercial' },
  { id: 'trials', segment: 'trials', label: 'Trials', mockOnly: true },
  { id: 'historico', segment: 'historico', label: 'Histórico', mockOnly: true },
];

/** Billing tabs that redirect to the dedicated inadimplência module. */
export const BILLING_INADIMPLENCIA_REDIRECT_TABS = new Set(['inadimplencia', 'regua']);

export const INADIMPLENCIA_TABS = [
  { id: 'regua', segment: null, label: 'Régua' },
  { id: 'lojas', segment: 'lojas', label: 'Lojas' },
  { id: 'historico', segment: 'historico', label: 'Histórico' },
];

export const INTEGRATIONS_TABS = [
  { id: 'overview', segment: null, label: 'Visão geral' },
  { id: 'tokens', segment: 'tokens', label: 'Tokens' },
  { id: 'apps', segment: 'apps', label: 'Apps' },
  { id: 'webhooks', segment: 'webhooks', label: 'Webhooks' },
  { id: 'logs', segment: 'logs', label: 'Logs' },
  { id: 'security', segment: 'seguranca', label: 'Segurança' },
];

export const OBSERVABILIDADE_TABS = [
  { id: 'overview', segment: null, label: 'Visão geral' },
  { id: 'incidentes', segment: 'incidentes', label: 'Incidentes' },
  { id: 'tenants', segment: 'tenants', label: 'Tenants' },
  { id: 'servicos', segment: 'servicos', label: 'Serviços' },
  { id: 'celery', segment: 'filas', label: 'Filas' },
  { id: 'opensearch', segment: 'opensearch', label: 'OpenSearch' },
  { id: 'redis', segment: 'redis', label: 'Redis' },
  { id: 'postgres', segment: 'postgres', label: 'Postgres' },
  { id: 'storage', segment: 'storage', label: 'Storage' },
  { id: 'errors', segment: 'erros', label: 'Erros' },
  { id: 'perf', segment: 'performance', label: 'Performance' },
];

export const REPAIRS_TABS = [
  { id: 'diagnostico', segment: null, label: 'Diagnóstico' },
  { id: 'repairs', segment: 'disponiveis', label: 'Disponíveis' },
  { id: 'historico', segment: 'historico', label: 'Histórico' },
  { id: 'playbooks', segment: 'playbooks', label: 'Playbooks' },
  { id: 'ferramentas', segment: 'ferramentas', label: 'Ferramentas' },
];

export const TICKETS_TABS = [
  { id: 'inbox', segment: null, label: 'Inbox' },
  { id: 'todos', segment: 'todos', label: 'Todos' },
  { id: 'meus', segment: 'meus', label: 'Meus' },
  { id: 'unassigned', segment: 'nao-atribuidos', label: 'Não atribuídos' },
  { id: 'sla-risk', segment: 'sla-em-risco', label: 'SLA em risco' },
  { id: 'macros', segment: 'macros', label: 'Macros' },
  { id: 'sla-rules', segment: 'regras-sla', label: 'Regras SLA' },
];

export const KB_TABS = [
  { id: 'articles', segment: null, label: 'Artigos' },
  { id: 'categories', segment: 'categorias', label: 'Categorias' },
  { id: 'searches', segment: 'buscas-sem-resultado', label: 'Buscas sem resultado' },
  { id: 'feedback', segment: 'feedback', label: 'Feedback' },
];

export const SUPPORT_REPORTS_TABS = [
  { id: 'overview', segment: null, label: 'Visão geral' },
  { id: 'sla', segment: 'sla', label: 'SLA' },
  { id: 'team', segment: 'equipe', label: 'Equipe' },
  { id: 'tenants', segment: 'tenants', label: 'Tenants' },
  { id: 'kb', segment: 'base-conhecimento', label: 'Base de conhecimento' },
];

export const NOTIFICACOES_TABS = [
  { id: 'inbox', segment: null, label: 'Inbox' },
  { id: 'criticas', segment: 'criticas', label: 'Críticas' },
  { id: 'regras', segment: 'regras', label: 'Regras', mockOnly: true },
  { id: 'historico', segment: 'historico', label: 'Histórico', mockOnly: true },
  { id: 'prefs', segment: 'preferencias', label: 'Preferências', mockOnly: true },
];

export const MODULE_TAB_CONFIG = {
  cockpit: {
    basePath: (ctx) => `/lojas/${encodeURIComponent(ctx.tenantId)}/cockpit`,
    tabs: COCKPIT_TABS,
    defaultTab: 'overview',
    page: 'cockpit',
  },
  billing: {
    basePath: () => '/billing',
    tabs: BILLING_TABS,
    defaultTab: 'overview',
    page: 'billing',
  },
  inadimplencia: {
    basePath: () => '/inadimplencia',
    tabs: INADIMPLENCIA_TABS,
    defaultTab: 'regua',
    page: 'inadimplencia',
  },
  integrations: {
    basePath: () => '/integracoes',
    tabs: INTEGRATIONS_TABS,
    defaultTab: 'overview',
    page: 'integrations',
  },
  observabilidade: {
    basePath: () => '/observabilidade',
    tabs: OBSERVABILIDADE_TABS,
    defaultTab: 'overview',
    page: 'observabilidade',
  },
  repairs: {
    basePath: () => '/repairs',
    tabs: REPAIRS_TABS,
    defaultTab: 'diagnostico',
    page: 'repairs',
  },
  tickets: {
    basePath: () => '/suporte/tickets',
    tabs: TICKETS_TABS,
    defaultTab: 'inbox',
    page: 'tickets',
  },
  'knowledge-base': {
    basePath: () => '/suporte/base-conhecimento',
    tabs: KB_TABS,
    defaultTab: 'articles',
    page: 'knowledge-base',
  },
  'support-reports': {
    basePath: () => '/suporte/relatorios',
    tabs: SUPPORT_REPORTS_TABS,
    defaultTab: 'overview',
    page: 'support-reports',
  },
  'notificacoes-ops': {
    basePath: () => '/notificacoes',
    tabs: NOTIFICACOES_TABS,
    defaultTab: 'inbox',
    page: 'notificacoes-ops',
  },
};

/**
 * Build canonical path for a module tab.
 */
export function buildTabPath(moduleKey, tabId, ctx = {}) {
  const cfg = MODULE_TAB_CONFIG[moduleKey];
  if (!cfg) return null;
  const byId = indexById(cfg.tabs);
  const tab = byId[tabId] || byId[cfg.defaultTab];
  const base = cfg.basePath(ctx);
  if (!tab || tab.segment == null) return base;
  return `${base}/${tab.segment}`;
}

/**
 * Resolve tab id from an optional path segment.
 * Unknown segment → default tab + needsCanonicalize.
 */
export function resolveTabFromSegment(moduleKey, segment) {
  const cfg = MODULE_TAB_CONFIG[moduleKey];
  if (!cfg) return { tabId: null, needsCanonicalize: false, invalid: true };

  if (segment == null || segment === '') {
    return { tabId: cfg.defaultTab, needsCanonicalize: false, invalid: false };
  }

  const bySeg = indexBySegment(cfg.tabs);
  const tab = bySeg[segment];
  if (!tab) {
    return { tabId: cfg.defaultTab, needsCanonicalize: true, invalid: true };
  }
  return { tabId: tab.id, needsCanonicalize: false, invalid: false };
}

export function getTabSegment(moduleKey, tabId) {
  const cfg = MODULE_TAB_CONFIG[moduleKey];
  if (!cfg) return null;
  const tab = indexById(cfg.tabs)[tabId];
  return tab ? tab.segment : null;
}

export function listModuleTabIds(moduleKey, { includeMockOnly = true } = {}) {
  const cfg = MODULE_TAB_CONFIG[moduleKey];
  if (!cfg) return [];
  return cfg.tabs
    .filter((t) => includeMockOnly || !t.mockOnly)
    .map((t) => t.id);
}

export function getModuleTabConfig(moduleKey) {
  return MODULE_TAB_CONFIG[moduleKey] || null;
}
